import { BaseAdapter } from './BaseAdapter';
import { 
  WalletInfo, 
  ConnectOptions, 
  TransactionRequest, 
  WalletType 
} from '../core/types';
import { 
  WalletConnectionError,
  TransactionError,
  UserRejectedError 
} from '../core/errors';
import { BNB_MAINNET, BNB_TESTNET } from '../core/chains';

// WalletConnect imports
import { EthereumProvider } from '@walletconnect/ethereum-provider';

export class CapacitorAdapter extends BaseAdapter {
  private walletConnectProvider: any = null;
  private projectId: string;
  private metadata: any;

  constructor(projectId: string, metadata?: any) {
    super();
    this.projectId = projectId;
    this.metadata = metadata || {
      name: 'BNB Wallet Connect',
      description: 'Connect to BNB Chain wallets',
      url: 'https://example.com',
      icons: ['https://example.com/icon.png']
    };
  }

  async connect(options?: ConnectOptions): Promise<WalletInfo> {
    try {
      // Initialize WalletConnect provider
      this.walletConnectProvider = await EthereumProvider.init({
        projectId: this.projectId,
        chains: [56, 97], // BNB Mainnet and Testnet chain IDs
        showQrModal: options?.showQrModal !== false, // Default to true
        metadata: this.metadata,
        rpcMap: {
          56: BNB_MAINNET.rpcUrls[0],
          97: BNB_TESTNET.rpcUrls[0],
        },
      });

      // Set up event listeners
      this.setupWalletConnectListeners();

      // Connect to wallet
      const accounts = await this.walletConnectProvider.enable();
      
      if (accounts.length === 0) {
        throw new UserRejectedError();
      }

      this._address = accounts[0];
      this._chainId = `0x${this.walletConnectProvider.chainId.toString(16)}`;
      this._isConnected = true;
      this._walletType = WalletType.WALLETCONNECT;
      this._provider = this.walletConnectProvider;

      return {
        address: this._address!,
        chainId: this._chainId!,
        provider: this.walletConnectProvider,
        isConnected: true,
        walletType: WalletType.WALLETCONNECT,
      };
    } catch (error: any) {
      if (error.message?.includes('User rejected')) {
        throw new UserRejectedError();
      }
      throw new WalletConnectionError(
        error.message || 'Failed to connect via WalletConnect'
      );
    }
  }

  private setupWalletConnectListeners(): void {
    if (!this.walletConnectProvider) return;

    this.walletConnectProvider.on('accountsChanged', (accounts: string[]) => {
      this.handleAccountsChanged(accounts);
    });

    this.walletConnectProvider.on('chainChanged', (chainId: number) => {
      this.handleChainChanged(`0x${chainId.toString(16)}`);
    });

    this.walletConnectProvider.on('disconnect', () => {
      this.handleDisconnect();
    });

    this.walletConnectProvider.on('session_delete', () => {
      this.handleDisconnect();
    });
  }

  async disconnect(): Promise<void> {
    if (this.walletConnectProvider) {
      try {
        await this.walletConnectProvider.disconnect();
      } catch (error) {
        console.error('Error disconnecting WalletConnect:', error);
      }
      this.walletConnectProvider = null;
    }
    this.handleDisconnect();
  }

  async switchChain(chainId: string): Promise<void> {
    if (!this.walletConnectProvider) {
      throw new WalletConnectionError('WalletConnect not connected');
    }

    try {
      await this.walletConnectProvider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId }],
      });
      this._chainId = chainId;
    } catch (error: any) {
      if (error.code === 4001) {
        throw new UserRejectedError('User rejected chain switch');
      }
      throw error;
    }
  }

  async sendTransaction(tx: TransactionRequest): Promise<string> {
    if (!this.walletConnectProvider || !this._isConnected) {
      throw new TransactionError('Wallet not connected');
    }

    try {
      const txHash = await this.walletConnectProvider.request({
        method: 'eth_sendTransaction',
        params: [{
          from: this._address,
          to: tx.to,
          value: tx.value,
          data: tx.data || '0x',
          gas: tx.gasLimit,
          gasPrice: tx.gasPrice,
        }],
      });

      return txHash;
    } catch (error: any) {
      if (error.code === 4001 || error.message?.includes('User rejected')) {
        throw new UserRejectedError('User rejected transaction');
      }
      throw new TransactionError(error.message || 'Transaction failed');
    }
  }

  async signMessage(message: string): Promise<string> {
    if (!this.walletConnectProvider || !this._isConnected) {
      throw new WalletConnectionError('Wallet not connected');
    }

    try {
      const signature = await this.walletConnectProvider.request({
        method: 'personal_sign',
        params: [message, this._address],
      });

      return signature;
    } catch (error: any) {
      if (error.code === 4001 || error.message?.includes('User rejected')) {
        throw new UserRejectedError('User rejected signature request');
      }
      throw error;
    }
  }
}